import React, { useEffect, useState }  from 'react';
import { useAppContext } from '../libs/contextLib';
import { userStates, setUserEmail } from '../libs/userLib';
import { Auth, Hub } from 'aws-amplify';
import { useMatomo } from '@jonkoops/matomo-tracker-react'
import { UiError } from '../libs/errorLib';
import { useNavigate } from "react-router-dom";
import { RedirectInfo } from '../libs/redirectInfo';
import { setUserFederatedInfo } from '../libs/userLib';
import { isAbsoluteURL } from '../libs/urllib';
import SelectProfile from './Selectprofile';
import { getUserProfilesFromSession } from '../libs/userLib';
import config from '../config';

/**
 * Component that will listen to the auth hub and set user info and
 * redirect to appropriate pages.
 */
export default function AuthManager() {
  const { redirectInfo, setRedirectInfo }           = useAppContext();
  const { userState, setUserState }                 = useAppContext();
  const { user, setUser }                           = useAppContext();
  const { setUserType }                             = useAppContext();
  const { setUserIsFederated }                      = useAppContext();
  const { setProviderName }                         = useAppContext();
  const { setUserDepartment }                       = useAppContext();
  const { setError }                                = useAppContext();
  const { setAuthRedirecting }                      = useAppContext();
  const [ showProfileSelect, setShowProfileSelect ] = useState(false);
  const [ profiles, setProfiles ]                   = useState([]);
  const [ selectedProfile, setSelectedProfile ]     = useState(null);
  const navigate                                    = useNavigate();
  const { pushInstruction, trackEvent }  = useMatomo();

  // When we get a profile set, navigate to the remembered link.
  useEffect(
    () => {
      // If profile changes we'll always hide the profile select.
      setShowProfileSelect(false);
      if(typeof(selectedProfile) === 'string') {
        if (selectedProfile === '') {
          // Main profile was selected (no uid)
          redirectWithTokens();
        }
        else {
          // If we have a valid profile redirect the user.
          redirectWithTokens(`&prf=${selectedProfile}`);
        }
      }
    },
    // eslint-disable-next-line
    [selectedProfile]
  );

  async function redirectWithTokens(profileParam = '') {
    // Get the session.
    const session      = await Auth.currentSession();
    const idToken      = session.getIdToken().jwtToken;
    const accessToken  = session.getAccessToken().jwtToken;
    const refreshToken = session.refreshToken.token;
    setAuthRedirecting(true);
    // Build the url.
    // Build state separately first.
    let stateParam = '';
    if(typeof redirectInfo.state === 'string' && redirectInfo.state !== '') {
      stateParam = `&state=${redirectInfo.state}`;
    }
    window.location = `${
      redirectInfo.redirect
    }?access_token=${
      accessToken
    }&identity_token=${
      idToken
    }&refresh_token=${
      refreshToken
    }${
      stateParam
    }${profileParam}`;
  }

  function handleProfileSelection(uid) {
    // Unshow the profile selection.
    setSelectedProfile(uid);
    setShowProfileSelect(false);
  }

  function setUserStateWrapper(state) {
    // Only do something if it actually changed.
    if(state !== userState) {
      // Fire off an event to matomo.
      trackEvent({
        category: 'identity',
        action: 'change state',
        name: state,
      });
      setUserState(state);
    }
  }

  /**
   * Set user and user type as int.
   *
   * @param {*} user
   *    User to set.
   */
  function setUserAndType(user) {
    try {
      // Set the user.
      setUser(user);
      // And then get the session to get the type.
      let typeInt = 0;
      // This is for a normal user.
      if (
        user &&
        user.hasOwnProperty('signInUserSession') &&
        user.signInUserSession !== null &&
        user.signInUserSession.hasOwnProperty('idToken') &&
        user.signInUserSession.idToken !== null &&
        user.signInUserSession.idToken.hasOwnProperty('payload') &&
        user.signInUserSession.idToken.payload !== null &&
        user.signInUserSession.idToken.payload.hasOwnProperty('custom:type')
      ) {
        try {
          typeInt = parseInt(user.signInUserSession.idToken.payload['custom:type'], 2);
        }
        catch(error) {
          // Don't care. Just don't set it.
          console.error(error)
        }
      }
      // And for a federated user.
      if (
        user &&
        user.hasOwnProperty('attributes') &&
        user.attributes !== null &&
        user.attributes.hasOwnProperty('custom:type')
      ) {
        try {
          typeInt = parseInt(user.attributes['custom:type'], 2);
        }
        catch(error) {
          // Don't care. Just don't set it.
          console.error(error)
        }
      }
      setUserType(typeInt);
      // Finally set the email address for tracking.
      setUserEmail((userEmail) => {
        pushInstruction('setUserId', userEmail);
      });
    }
    catch(error) {
      // Don't care. Just don't set it.
      console.error(error)
    }
  }

  /**
   * Function that uses Amplify Auth to fetc and set the user object.
   */
  async function getCurrentUser(bypassCache = false) {
    // First get the user.
    Auth.currentAuthenticatedUser({ bypassCache: bypassCache })
    .then(
      user => {
        setUserAndType(user);
        setUserStateWrapper(userStates.CONFIRMED);
        setError(new UiError('auth', '', 'resolved'));
      }
    )
    .catch(
      error => {
        // If this errors we have no signed in user.
        setUserAndType(null);
        setUserStateWrapper(userStates.UNAUTHENTICATED);
      }
    );
    // And determine whether they're federated or not.
    setUserFederatedInfo(
      (isFederated, department, providerName) => {
        setUserIsFederated(isFederated);
        setUserDepartment(department);
        setProviderName(providerName);
      }
    );
  }

  async function forceSessionRefresh() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const session = await Auth.currentSession();
      user.refreshSession(session.refreshToken, (err, session) => {
        console.log('session refreshed', err, session);
      });
    } catch (e) {
      console.error('Unable to refresh Token', e);
    }
  }

  // We handle all auth events here.
  const authEventListener = ({ payload: { event, data }}) => {
    switch (event) {
      case "signIn":
        getCurrentUser();
        break;
      case 'signUp':
        // Check whether we're UserNotConfirmedException.
        if (data.userConfirmed) {
          getCurrentUser();
        }
        else {
          // We'll need to confirm.
          setUserStateWrapper(userStates.UNCONFIRMED);
          setUserAndType(data.user);
        }
        getCurrentUser();
        break;
      case "signUp_failure":
        setUserAndType(null);
        setUserStateWrapper(userStates.UNAUTHENTICATED);
        setError(new UiError('auth', data.message, 'danger'));
        break;
      case "signIn_failure":
        // Is this an unverified user?
        if (data.code === "UserNotConfirmedException") {
          setUserStateWrapper(userStates.UNCONFIRMED);
        }
        else {
          setUserStateWrapper(userStates.UNAUTHENTICATED);
          setUserAndType(null);
          if (
            data.code === 'NotAuthorizedException' &&
            data.message === 'Temporary password has expired and must be reset by an administrator.'
          ) {
            setError(
              new UiError(
                'auth',
                'Temporary password has expired and must be reset. Please click "I don\'t have login details".',
                'danger'
              )
            );
          }
          else {
            setError(new UiError('auth', data.message, 'danger'));
          }
        }
        break;
      case "cognitoHostedUI":
        getCurrentUser();
        break;
      case "customOAuthState":
        // Try to parse it.
        let redirectInfo = new RedirectInfo();
        redirectInfo.fillFromEncodedString(data);
        // This will be picked up useEffect.
        setRedirectInfo(redirectInfo);
        break;
      case "signOut":
        setUserAndType(null);
        setUserStateWrapper(userStates.UNAUTHENTICATED);
        setError(new UiError('auth', '', 'resolved'));
        break;
      case "oAuthSignOut":
        setUserAndType(null);
        setUserStateWrapper(userStates.UNAUTHENTICATED);
        setError(new UiError('auth', '', 'resolved'));
        break;
      case 'tokenRefresh':
        break;
      case 'tokenRefresh_failure':
        setUserAndType(null);
        setUserStateWrapper(userStates.UNAUTHENTICATED);
        setError(new UiError('auth', 'Session has expired or was globally signed out.', 'info'));
        break;
      case "forgotPassword":
        break;
        case "forgotPasswordSubmit":
          break;
      case 'configured':
        break;
      case 'parsingCallbackUrl':
        break;
      case 'completeNewPassword_failure':
        setError(
          new UiError(
            'auth',
            data.message,
            'danger'
          )
        );
        break;
      default:
        console.error({
          message: 'Received unexpected response from authenticator.',
          event: event,
          data: data
        });
        setError(new UiError('auth', `Received unexpected response from authenticator: ${event}`, 'danger'));
    }
  }

  /**
   * As not all auth related event generate events on the hub we create some
   * manually.
   *
   * @param {*} payload of the hub event. 
   */
  const customEventListener = ({ payload: { event, data }}) => {
    switch (event) {
      case "new_password_required":
        setUserAndType(data.user);
        setUserStateWrapper(userStates.FORCE_CHANGE_PASSWORD);
        break;
      case "confirm":
        console.log("Received custom event confirm.");
        setUserStateWrapper(userStates.UNAUTHENTICATED);
        if(isAbsoluteURL(config.links.loginUserType)) {
          window.location = config.links.loginUserType;
        }
        else {
          navigate(config.links.loginUserType);
        }
        break;
      case "refreshUser":
        forceSessionRefresh();
        getCurrentUser(true);
        break;
      case "userDeleted":
        setUserStateWrapper(userStates.UNAUTHENTICATED);
        setUserAndType(null);
        navigate("/userdeleted");
        break;
      default:
        // Do nothing
        break;
    }
  }

  /**
   * This function redirects the user to the location specified in the
   * redirectInfo.
   */
  async function redirectToRedirectUri() {
    // Get the session.
    const session      = await Auth.currentSession();
    const idToken      = session.getIdToken().jwtToken;
    const accessToken  = session.getAccessToken().jwtToken;
    const refreshToken = session.refreshToken.token;
    const userProfiles = getUserProfilesFromSession(session)
    setProfiles(userProfiles);
    // If we have profiles, we'll want the user to select one.
    // After they have the redirect will be handled in the the useEffect
    // listening for changes in selected profile.
    if(userProfiles.length > 0) {
      setShowProfileSelect(true);
      return;
    }
    setAuthRedirecting(true);
    // Build the url.
    // Build state separately first.
    let stateParam = '';
    if(typeof redirectInfo.state === 'string' && redirectInfo.state !== '') {
      stateParam = `&state=${redirectInfo.state}`;
    }
    window.location = `${
      redirectInfo.redirect
    }?access_token=${
      accessToken
    }&identity_token=${
      idToken
    }&refresh_token=${
      refreshToken
    }${
      stateParam
    }`;
  }

  /**
   * Runs the first time we render.
   */
  useEffect(() => {
    // Initialise the listener for auth channel, and custom confirm channel..
    Hub.listen('auth', authEventListener);
    Hub.listen('custom', customEventListener);
    // if the user in initialising, try and get the user.
    if(userState === userStates.INITIALISING) {
      getCurrentUser();
    }
    // Check whether we have a redirect or state.
    const urlParams = new URLSearchParams(window.location.search);
    setRedirectInfo(
      new RedirectInfo(
        urlParams.get('redirect_uri'),
        urlParams.get('state')
      )
    )
    // eslint-disable-next-line
  },[]);

  /**
   * Runs when user or redirectInfo changes.
   * Will redirect on the right state.
  */
  useEffect(() => {
    if(
      user !== null &&
      userState === userStates.CONFIRMED &&
      redirectInfo.redirect !== null
    ) {
      // Is the redirect whitelisted?
      // If the url is to a local path we'll allow it even if not whitelisted.
      if (
        redirectInfo.isAbsolutePath ||
        config.oauth.redirect_whitelist.includes(redirectInfo.redirect)
      ) {
        trackEvent({
          category: 'redirect',
          action: 'success',
          name: redirectInfo.redirect,
        });
        redirectToRedirectUri();
      }
      else {
        console.error(`The portal was asked to redirect you to a location that is not on the list of approved locations. This was prevented. Please contact support relating this error message with the following attempted redirect: "${redirectInfo.redirect}"`);
        setError(new UiError(
          'redirect',
          `The portal was asked to redirect you to a location that is not on the list of approved locations. This was prevented as it is a potential security risk. Please contact support relating this error message with the following attempted redirect: "${redirectInfo.redirect}"`,
          'persist'
        ));
        trackEvent({
          category: 'redirect',
          action: 'unauthorised',
        });
      }
    }
    // eslint-disable-next-line
  }, [user, userState, redirectInfo]);

  // We'll render an empty component.
  return(
    <SelectProfile
      show = {showProfileSelect}
      profiles = { profiles }
      callback = { handleProfileSelection }
    />
  );

}